import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, Zap } from 'lucide-react'
import { pillarLinks } from '@/data/pillarPages'

const NAV_LINKS = [
  { to: '/',         label: 'Home',          exact: true },
  { to: '/#system',  label: 'How It Works'               },
  { to: '/services', label: 'Capabilities'               },
  { to: '/blog',     label: 'Blog'                       },
  { to: '/brand-manifesto', label: 'Manifesto'           },
  { to: '/contact',  label: 'Contact'                    },
]

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled]  = useState(false)
  const { pathname } = useLocation()

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false) }, [pathname])

  // Sticky header shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-dark-950/95 backdrop-blur-md border-b border-white/10 shadow-xl shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-white group-hover:text-brand-300 transition-colors">
              ANVI <span className="text-brand-400">Solutions</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'text-white bg-white/10'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="group relative">
              <button className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all duration-150">
                Solutions
              </button>
              <div className="invisible absolute right-0 top-full z-50 w-72 pt-3 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100">
                <div className="rounded-xl border border-white/10 bg-dark-950/95 p-2 shadow-2xl shadow-black/40 backdrop-blur-xl">
                  {pillarLinks.map(({ to, label }) => (
                    <Link key={to} to={to} className="block rounded-lg px-3 py-2.5 text-sm text-white/60 transition hover:bg-white/5 hover:text-brand-400">
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link to="/contact" className="btn-primary ml-4 text-sm py-2">
              Book an Audit
            </Link>
          </nav>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <nav className="md:hidden border-t border-white/10 py-4 space-y-1 animate-fade-in">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
            <div className="my-3 border-t border-white/10 pt-3">
              <p className="px-4 pb-2 font-mono text-[10px] uppercase tracking-widest text-white/35">Solutions</p>
              {pillarLinks.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="block px-4 py-2 text-sm text-white/60 transition-colors hover:text-brand-400"
                >
                  {label}
                </NavLink>
              ))}
            </div>
            <div className="pt-2 pb-1 px-1">
              <Link to="/contact" className="btn-primary w-full justify-center text-sm">
                Book an Audit
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  )
}

